import { create } from 'zustand';
import type { ConfirmedProfile, ProfileSuggestion } from '../api/client';

/**
 * What the Collect step learned, handed to the Confirm step.
 *
 * Kept out of useAuditStore on purpose: this is a transient inference the user
 * has not accepted yet. Only the fields they confirm get written back to
 * project_info and the questionnaire.
 */
interface OnboardingState {
  suggestion: ProfileSuggestion | null;
  /** What the user confirmed, in backend field names. Saved against the
   *  COLLECTION chat by Confirm (so the agent sees it via `_source_chat_id`)
   *  and re-saved against the AUDIT chat once that exists — the report
   *  compiler reads the questionnaire from the chat it compiles. */
  confirmedAnswers: Record<string, string | string[]> | null;
  /** The confirmed form itself, re-saved with the answers (VIBE-2035). */
  confirmedProfile: ConfirmedProfile | null;
  setSuggestion: (suggestion: ProfileSuggestion) => void;
  setConfirmedAnswers: (answers: Record<string, string | string[]>) => void;
  setConfirmedProfile: (profile: ConfirmedProfile) => void;
  reset: () => void;
}

export const useOnboardingStore = create<OnboardingState>((set) => ({
  suggestion: null,
  confirmedAnswers: null,
  confirmedProfile: null,
  setSuggestion: (suggestion) => set({ suggestion }),
  setConfirmedAnswers: (confirmedAnswers) => set({ confirmedAnswers }),
  setConfirmedProfile: (confirmedProfile) => set({ confirmedProfile }),
  reset: () => set({ suggestion: null, confirmedAnswers: null, confirmedProfile: null }),
}));

const DRAFT_PREFIX = 'confirm_draft:';

/**
 * Unsaved Confirm edits, per chat, in sessionStorage — so Back to /connect and
 * a reload of /confirm both return the user to what they typed (VIBE-2035).
 * Keyed by chat: one audit's draft must never prefill another's.
 */
export function readConfirmDraft(chatId: string | null): ConfirmedProfile | null {
  if (!chatId) return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_PREFIX + chatId);
    return raw ? (JSON.parse(raw) as ConfirmedProfile) : null;
  } catch {
    return null;
  }
}

export function writeConfirmDraft(chatId: string | null, draft: ConfirmedProfile | null): void {
  if (!chatId) return;
  try {
    if (draft) sessionStorage.setItem(DRAFT_PREFIX + chatId, JSON.stringify(draft));
    else sessionStorage.removeItem(DRAFT_PREFIX + chatId);
  } catch {
    /* storage unavailable — the form still works, edits just don't survive a reload */
  }
}

export function clearConfirmDrafts(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(DRAFT_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* noop */
  }
}
